<template>
  <div class="__HeroChart">
    <div class="__HeroChart-Content">
      <!-- Main content - Left side -->
      <div class="__HeroChart-Content-LeftSide">
        <CSection class="__HeroChart-Control">
          <div class="__HeroChart-Control-LeftSide">
            <DateRange
              class="mr-md"
              ref="data-range"
              :range="dateRange"
              :quick-range-options="quickRangeOptions"
              :min-date="minDate"
              :max-date="maxDate"
              :granularity="currentGranularity"
              @change="onChangeDateRange"
            />

            <CSelector
              min-width="120px"
              :options="granularityOptions"
              v-model="currentGranularity"
              map-options
              emit-value
              :use-input="false"
              hide-bottom-space
              @change="onChangeGranularity"
            />
            <HelpPopover placement="bottom" v-if="currentGranularity === 'monthly'">{{
              $t('monthlyDelayNotice')
            }}</HelpPopover>
          </div>

          <div class="__HeroChart-Control-RightSide">
            <!-- Separator -->
            <div class="__HeroChart-ControlSeparator" />

            <q-btn-toggle
              :value="chartType"
              toggle-color="primary"
              flat
              :options="chartTypeOptions"
              @input="onChangeChartType"
            >
              <template v-slot:stacked-bar>
                <CTooltip :message="$t('Stacked')" position="bottom" :delay="500">
                  <CIcon name="stacked_bar_chart" material />
                </CTooltip>
              </template>
              <template v-slot:bar>
                <CTooltip :message="$t('Bar')" position="bottom" :delay="500">
                  <CIcon name="bar_chart" material />
                </CTooltip>
              </template>
              <template v-slot:line>
                <CTooltip :message="$t('Line')" position="bottom" :delay="500">
                  <CIcon name="show_chart" material />
                </CTooltip>
              </template>
            </q-btn-toggle>

            <!-- Separator -->
            <div class="__HeroChart-ControlSeparator" />

            <!-- Action buttons -->
            <CButton
              icon="filter_list"
              round
              flat
              :color="showFilters ? 'primary' : undefined"
              margin
              :tooltip="$t('Group/Filter')"
              @click="showFilters = !showFilters"
            />

            <ActionMenu
              :conditions="chartConditions"
              @export-data="onExportData"
              @export-image="onExportImage"
              @click-chart-preferences="onClickChartPreferences"
              v-if="actionMenu"
            />
          </div>
        </CSection>

        <div class="__HeroChart-Main">
          <!-- Charts -->
          <XYChart
            ref="chart"
            name="dashboard-chart"
            :locale="currentLocale"
            :data="currentData"
            :category="currentCategory"
            :granularity="currentGranularity"
            :conditions="chartConditions"
            :total-id="totalId"
            :total-label="totalLabel"
            :series="filterTotalSeries(convertedCurrentSeries)"
            :chart-type="chartType"
            :tooltip-value-prefix="currencySymbol"
            @datavalidated="onDataValidated"
            @overItem="onOverItem"
            @click-item="onClickItem"
            @appeared="onAppeared"
          />
          <CLoading :loading="loading" />
        </div>
      </div>

      <!-- Main content - Right side -->
      <div class="__HeroChart-Content-RightSide">
        <CTransition name="slide-right">
          <div class="__HeroChart-Filters" v-if="showFilters">
            <!-- SLOT: filters-prepend -->
            <slot name="filters-prepend" />

            <template v-if="selectableCategory">
              <CTitle class="mb-xs">{{ $t('Group by') }}</CTitle>
              <!-- Selectable categories -->
              <CSection>
                <CSelector
                  ref="categoryDropdown"
                  :options="categoryOptions"
                  v-model="currentCategory"
                  map-options
                  emit-value
                  :use-input="false"
                  :disabled="loading"
                  hide-bottom-space
                  @change="onChangeCategory"
                />
              </CSection>

              <!-- Gab: dropdown for tag keys -->
              <CSection v-if="currentCategory === 'tags'">
                <div class="tag-key-container">
                  <CTitle class="mb-xs">{{ $t('Tags') }}</CTitle>
                  <CSelector
                    ref="tagKeyDropdown"
                    :options="sortedTagKeyOptions"
                    v-model="truncatedTagKey"
                    map-options
                    emit-value
                    option-value="value"
                    option-label="label"
                    :use-input="false"
                    :disabled="loading"
                    hide-bottom-space
                    @change="onChangeTagKey"
                  />
                </div>
              </CSection>

              <CSeparator spaced />
            </template>
            <!-- Categories list -->
            <CSection>
              <SeriesList
                v-model="currentSeries[currentCategory]"
                :series="sortedSeries"
                series-label="name"
                series-id="id"
                series-value="value"
                :total-id="totalId"
                filterable
                :filter-key="['name']"
                :search-placeholder="$t('Search by {label} name.', { label: $t(currentCategory) })"
                clickable
                dense
                :disabled="loading"
                @select-all="onSelectAll"
              />
              <!-- <Checkbox
                v-model="checkAllAccounts"
                :label="$t(`AllAccounts`)"
                color="primary"
                @input="onInputCheckAllAccounts"
                size="xs"
              />
              <div class="checkbox-group">
                <OptionGroup
                  :group="activeFilters['region']"
                  :options="regionOptions"
                  @input="onChangeCheckedRegions"
                  color="primary"
                  size="xs"
                />
              </div> -->
            </CSection>

            <CSeparator spaced />

            <div class="__HeroChart-FiltersFooter">
              <CButton
                color="primary"
                full-width
                :loading="loading"
                @click="onSubmit"
                tooltip-position="top"
              >
                {{ $t('Apply filter') }}
              </CButton>
            </div>
          </div>
        </CTransition>
      </div>
    </div>

    <!-- Data table -->
    <div class="__HeroChart-DataTable">
      <ChartDataTable
        :data="chartData"
        :series="series"
        :currentSeries="currentTableSeries"
        :date-range="dateRange"
        :currency="chartConditions.currency"
        :granularity="currentGranularity"
        :category-label="$t(currentCategory)"
        :total-label="totalLabel"
        :locale="currentLocale"
        :filterItems="clickedItem"
        :show-total="chartConditions.showTotal"
        :conditions="chartConditions"
        :loading="loading"
        @deselect="onDeselectClickedItem"
        v-if="dataTable"
      />
    </div>

    <PreferencesDialog
      v-model="openPreferencesDialog"
      :is-on-dashboard="isOnDashboard"
      :conditions="chartConditions"
      :category-options="categoryOptions"
      @success="onChangeSettingSuccess"
      @update-preferences="updatePreferences"
    />
  </div>
</template>

<script>
/*
 * [Events]
 * @update-date-range -> function({ start: String, end: String }): Emitted when date range is updated.
 * @change-granularity -> function(): Emitted when granularity is changed.
 * @change-category -> function(): Emitted when category is changed.
 * @change-setting-success -> function(): Emitted when the setting change is successful.
 *
 * [Methods]
 * setInitialSeries() => void 0
 * initialGenerateChart() => void 0
 * generateChart() => void 0
 */

import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import { sortObject } from '@/lib/sorter';

// Chart functions
import {
  dataFormatter,
  seriesFormatter,
  addOthersData,
  calcOthersTotalAmount,
} from '@/components/CustomCharts/chartFn';

// Chart common lang
import chartLang from '@/components/CustomCharts/lang';

import { currencySymbol } from '@/lib/currency';
import XYChart from '@/components/CustomCharts/XYChart';
import DateRange from '@/components/CustomCharts/DateRange';
import ActionMenu from '@/components/CustomCharts/ActionMenu';
import SeriesList from '@/components/CustomCharts/SeriesList';
import ChartDataTable from '@/components/CustomCharts/ChartDataTable';
import PreferencesDialog from '@/components/CustomCharts/PreferencesDialog';
import CTransition from '@/components/uikit/CTransition';
import CSection from '@/components/uikit/CSection';
import CTitle from '@/components/uikit/CTitle';
import CSelector from '@/components/uikit/CSelector';
import CSeparator from '@/components/uikit/CSeparator';
import CButton from '@/components/uikit/CButton';
import CIcon from '@/components/uikit/CIcon';
import CDate from '@/components/uikit/CDate';
import CTooltip from '@/components/uikit/CTooltip';
import CLoading from '@/components/uikit/CLoading';
import HelpPopover from '@/components/uikit/HelpPopover';
import { analyzer } from '@/analyzer';
import { required } from 'vuelidate/lib/validators';

const messages = {
  en: {
    // Chart common lang
    ...chartLang.en,

    'Cost ({symbol})': 'Cost ({symbol})',
    'Total ({symbol})': 'Total ({symbol})',
    'Search by {label} name.': 'Search by {label} name.',
    'All {type}': 'All {type}',
    'Group/Filter': 'Group/Filter',
    'Group by': 'Group By',
    'Apply filter': 'Apply',
    Tags: 'Tags',
    services: 'Services',
    monthlyDelayNotice:
      'There will be a slight delay for your monthly graph to be in sync with your daily graph. If you notice some differences, try waiting for about 5 - 10mins before refreshing this page.',
  },
  ja: {
    // Chart common lang
    ...chartLang.ja,

    'Cost ({symbol})': 'コスト（{symbol}）',
    'Total ({symbol})': '合計（{symbol}）',
    'Search by {label} name.': '{label}名で検索',
    'All {type}': '全{type}',
    'Group/Filter': 'グループ/フィルター',
    'Group by': 'グループ化の条件',
    'Apply filter': 'フィルターを適用',
    Tags: 'タグ',
    services: 'サービス',
    monthlyDelayNotice:
      'データを同期するタイミングの都合で、日次グラフの当月の合計値と比較した際に月次グラフの値が低く表示されることがあります。差分が見られる場合は 5 - 10分待ってからページをリロードしてください。',
  },
};

export default Vue.extend({
  name: 'HeroChart',
  i18n: { messages },
  components: {
    XYChart,
    DateRange,
    ActionMenu,
    ChartDataTable,
    PreferencesDialog,
    CTransition,
    CSection,
    CTitle,
    CSelector,
    CSeparator,
    SeriesList,
    CButton,
    CIcon,
    CDate,
    CTooltip,
    CLoading,
    HelpPopover,
  },
  props: {
    initialSeries: {
      type: Array,
      default: () => [],
    },
    shouldResetOnMount: {
      type: Boolean,
      default: false,
    },

    name: {
      type: String,
      default: 'chart',
    },
    data: {
      type: [Array, Object],
      required: true,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    dateRange: {
      type: Object,
    },

    // Category
    categories: {
      type: Array,
      required: true,
    },

    selectableCategory: {
      type: Boolean,
    },

    granularity: {
      type: String,
      required: true,
    },

    conditions: {
      type: Object,
      required: true,
      default: () => {},
    },

    minDate: {
      type: Date,
    },

    maxDate: {
      type: Date,
    },

    currency: {
      type: String,
      required: true,
    },

    // Tools
    actionMenu: {
      type: Boolean,
    },

    dataTable: {
      type: Boolean,
    },

    isOnDashboard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Config
      supportCurrency: ['usd', 'jpy'],
      totalId: '#total',
      // Default filters number
      defaultSeriesCount: 5,

      // Chart data
      chartData: [],
      currentSeries: {},
      currentTableSeries: [],
      currentCategory: null,
      currentGranularity: 'daily',

      // Gab: current tag key selected
      selectedTagKey: null,

      // State
      showFilters: true,

      // Dialog
      openPreferencesDialog: false,

      /**
       * Data for other components
       */
      chartType: 'bar',
      chartTypeOptions: [
        {
          value: 'stacked',
          slot: 'stacked-bar',
        },
        {
          value: 'bar',
          slot: 'bar',
        },
        {
          value: 'line',
          slot: 'line',
        },
      ],

      granularityOptions: [
        { label: this.$t('Daily'), value: 'daily' },
        { label: this.$t('Monthly'), value: 'monthly' },
      ],

      // Event
      clickedItem: [],
    };
  },
  created() {
    /**
     * Set default chart conditions.
     */
    // this.currentCategory = this.chartConditions.category;
    
    const currentVendor = this.$store.state.vendor;

    if (currentVendor === 'aws') {
      this.currentCategory = 'productCode';
    } else if (currentVendor === 'azure' || currentVendor === 'gcp') {
      this.currentCategory = 'service';
    }

    this.currentGranularity = this.chartConditions.granularity;
  },
  computed: {
    ...mapGetters('user', {
      currentLocale: 'getLocale',
      valueKey: 'getCostType',
    }),

    ...mapGetters('reportFilters', {
      getActiveFilterData: 'getActiveFilterData',
      activeFilterId: 'getActiveFilter',
      tagKeyOptions: 'getTagNameOptions', // Gab: Getters for tag keys in vuex store
    }),

    ...mapState('reports_v2', {
      resourceType: 'resource',
    }),
    quickRangeOptions() {
      if (this.currentGranularity === 'daily') {
        return ['mtd', 'lastMonth'];
      } else {
        return ['mtd', 'lastMonth', 'ytd', '3months', '6months', '1year'];
      }
    },

    categoryLabel() {
      return this.$t(this.currentCategory);
    },

    totalLabel() {
      return this.$t('All {type}', {
        type: this.$t(this.currentCategory === 'service' ? 'services' : this.currentCategory),
      });
    },

    currentData() {
      let data;

      if (this.resourceType === 'report_filter') {
        const groupBy = this.currentCategory === 'service' ? 'productCode' : 'account';

        const currentData = Object.keys(this.data)
          .sort((a, b) => {
            const left = '' + a;
            const right = '' + b;
            return left.localeCompare(right);
          })
          .map(date => {
            const items = {};
            let total = 0;
            this.data[date].forEach(item => {
              total += item[this.valueKey];
              if (items[item[groupBy]]) {
                items[item[groupBy]] += item[this.valueKey];
              } else {
                items[item[groupBy]] = item[this.valueKey];
              }
            });
            return {
              date: moment(date, 'YYYY-MM-DD').toDate(),
              ...items,
              '#total': total,
            };
          });
        return currentData;
      }

      // Format data
      data = dataFormatter({
        data: this.data,
        start: this.dateRange.start,
        end: this.dateRange.end,
        granularity: this.granularity,
        valueKey: this.valueKey,
        category: this.currentCategory,
      });

      if (!this.currentSeries[this.currentCategory]) return data;

      // Add #others data
      const othersSeriesId = [];

      this.currentSeries[this.currentCategory].forEach((val, index) => {
        if (index > this.chartConditions.nonGroupingSeriesCount) {
          othersSeriesId.push(val);
        }
      });

      data = addOthersData(data, othersSeriesId);

      return data;
    },

    convertedCurrentSeries() {
      const targetSeriesIds = this.currentSeries[this.currentCategory];
      if (!targetSeriesIds) return [];
      if (!this.series.length) return [];

      const converted = [];
      const count = this.chartConditions.nonGroupingSeriesCount;

      for (let i = 0; i < targetSeriesIds.length; i++) {
        if (i > count) continue;

        const seriesItem = this.series.find(val => val.id === targetSeriesIds[i]);
        if (seriesItem) {
          converted.push(seriesItem);
        }

        // length - 1: targetSeriesIds has #total.
        if (targetSeriesIds.length - 1 > count) {
          if (i === count) {
            converted.push({
              name: 'Other',
              id: '#others',
              value: calcOthersTotalAmount(this.currentData),
            });
          }
        }
      }

      return converted;
    },

    series() {
      if (this.resourceType === 'report_filter') {
        let series = {};
        const groupBy = this.currentCategory === 'service' ? 'productCode' : 'account';

        Object.keys(this.data).forEach(date => {
          this.data[date].forEach(item => {
            if (series[item[groupBy]]) {
              series[item[groupBy]] = {
                ...series[item[groupBy]],
                value: series[item[groupBy]].value + item[this.valueKey],
              };
            } else {
              series[item[groupBy]] = {
                id: item[groupBy],
                name: item[groupBy],
                value: item[this.valueKey],
              };
            }
          });
        });
        const seriesArray = Object.keys(series).map(item => {
          return series[item];
        });
        return seriesArray;
      }
      return seriesFormatter(this.data, this.valueKey);
    },

    activeFilters() {
      const activeFilterData = this.getActiveFilterData({
        vendor: 'aws',
        filterId: this.activeFilterId,
      });
      if (activeFilterData) {
        let filters = { ...activeFilterData.awsOptions.filters[0].andFilters };
        Object.keys(filters).forEach(filter => {
          filters[filter] = filters[filter]?.replace('re:', '').split('|');
        });
        return filters;
      } else {
        return { region: [] };
      }
    },

    regionOptions() {
      return this.activeFilters
        ? this.activeFilters['region']?.map(region => ({
            label: region,
            value: region,
            tooltip: region,
          }))
        : [];
    },

    /**
     * This is only for initialize chart. Never update when you change some chart parameter.
     * @return {object}
     */
    chartConditions() {
      //const currency = this.getConfig('currency', 'usd');
      const currency = this.currency;

      return {
        name: this.name,
        dateRange: this.dateRange,
        currency: currency,
        valueKey: this.valueKey,
        category: this.getConfig('category', 'service'),
        granularity: this.getConfig('granularity', 'daily'),
        defaultChartType: this.getConfig('defaultChartType', 'stacked'),

        // Support categories.
        // service, account
        categories: this.categories,

        // Range
        defaultRange: this.getConfig('defaultRange', '30days'),

        // チャートアニメーション
        animated: this.getConfig('animated', true),
        // チャートのグループ化
        grouping: this.getConfig('grouping', true),

        // Axis
        xAxisTitle: `${this.$t('Cost ({symbol})', { symbol: currencySymbol(currency) })}`,
        xAxisLabel: 'date',
        xAxisFormat: this.currentLocale === 'ja' ? 'yyyy/MM/dd' : 'dd/MM/yyyy',
        yAxisFormat: '#,###.00####a',
        bigNumberPrefixes: [
          { number: 1e3, suffix: this.$t('K') },
          { number: 1e6, suffix: this.$t('M') },
          { number: 1e9, suffix: this.$t('B') },
        ],

        // Series
        defaultSeries: this.getConfig('defaultSeries', []),
        defaultSeriesCount: this.getConfig('defaultSeriesCount', this.defaultSeriesCount),
        nonGroupingSeriesCount: this.getConfig('nonGroupingSeriesCount', 5),

        // Total(ALL)
        showTotal: this.getConfig('showTotal', true),
        totalColor: this.getConfig('totalColor', '#999999'),
        totalStrokeType: this.getConfig('totalStrokeType', 'solid'), // solid, dashed
        totalLineWidth: this.getConfig('totalLineWidth', 2), // 1, 2, 3
        totalAxisTitle: `${this.$t('Total ({symbol})', {
          symbol: currencySymbol(currency),
        })}`,

        // Tooltip
        tooltipNumberFormat: currency === 'jpy' ? '#,###' : '#,###.00',

        // Others
        othersColor: this.getConfig('othersColor', '#999999'),

        // Style
        scrollbar: this.getConfig('scrollbar', false),

        // Export
        exportDataType: this.getConfig('exportDataType', 'html'),
        exportImageType: this.getConfig('exportImageType', 'svg'),
        exportPrefix: this.getConfig('exportPrefix', 'chart'),

        // Datatable
        defaultTableRowsPerPage: this.getConfig('defaultTableRowsPerPage', 10),
      };
    },

    currencySymbol() {
      return currencySymbol(this.chartConditions.currency);
    },

    // Gab: Add category option here for the Group-by dropdown
    categoryOptions() {
      const options = [
        { label: this.$t('productCode'), value: 'productCode' },
        { label: this.$t('service'), value: 'service' },
        { label: this.$t('account'), value: 'account' },
        { label: this.$t('region'), value: 'region' },
        { label: this.$t('instanceType'), value: 'instanceType' },
        { label: this.$t('tags'), value: 'tags' },
        // { label: this.$t('usageType'), value: 'usageType' },
        { label: this.$t('tag_resource'), value: 'resource' },
      ];

      return options.filter(i => {
        return this.categories.includes(i.value);
      });
    },

    // Gab: tracated tag key for display in UI
    truncatedTagKey() {
      const fullTagKey = this.$store.state.reports_v2.selectedTagKey;
      if (fullTagKey && fullTagKey.length > 25) {
        return `${fullTagKey.substring(0, 25)}...`;
      }
      return fullTagKey; // return truncated tag key in the UI
    },

    // Gab: sorting categories lists alphabetically
    sortedSeries() {
      if (!this.series.length) return [];

      switch (this.currentCategory) {
        case 'productCode':
        case 'service':
        case 'account':
        case 'region':
        case 'tags':
        case 'instanceType': {
          const selectedIds = this.currentSeries[this.currentCategory] || [];
          const selectedItems = [];
          const unselectedItems = [];

          this.series.forEach(item => {
            if (selectedIds.includes(item.id)) {
              selectedItems.push(item);
            } else {
              unselectedItems.push(item);
            }
          });

          const sortedSelected = selectedItems.sort((a, b) => b.value - a.value);
          const sortedUnselected = this.sortAlphabetically(unselectedItems);
          return [...sortedSelected, ...sortedUnselected];
        }
        default:
          return this.series;
      }
    },

    // Gab: sorting tag keys
    sortedTagKeyOptions() {
      if (!this.tagKeyOptions) return [];

      return [...this.tagKeyOptions]
        .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .map(tagKey => {
          return {
            label: this.simplifyTagKeyDisplay(tagKey), // For display in UI
            value: tagKey, // Preserve original value for functionality
          };
        });
    },
  },

  watch: {
    series(newSeries) {
      if (newSeries.length) {
        this.setInitialSeries();
      }
    },
    '$route.path'() {
      this.currentCategory = this.chartConditions.category;
    },
    // truncatedTagKey(newVal) {
    //   console.log('Truncated Tag Key:', newVal);
    // },
  },

  methods: {
    filterTotalSeries(series) {
      if (!series.length) return [];
      return series.filter(i => i.id !== this.totalId);
    },

    getConfig(key, defaultValue) {
      if (this.conditions[key] === null || this.conditions[key] === undefined) return defaultValue;
      return this.conditions[key];
    },

    async initialGenerateChart() {
      // Set chart style, granularity
      this.chartType = this.chartConditions['defaultChartType'];
      this.currentGranularity = this.chartConditions['granularity'];

      this.setInitialSeries();

      return this.generateChart();
    },

    sortAlphabetically(items) {
      return [...items].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },

    setInitialSeries() {
      Vue.set(this.currentSeries, this.currentCategory, []);

      /*
       * 最初に表示するシリーズをセットする
       * Set the series to be displayed first.
       */
      // Make series array by ID
      const series = this.series.map(i => {
        return i.id;
      });

      const setSeries = [];

      // デフォルトシリーズをセットする。
      // Set the default series.
      const defaultSeries = this.chartConditions.defaultSeries[this.currentCategory]
        ? this.chartConditions.defaultSeries[this.currentCategory]
        : [];
      defaultSeries.forEach(i => {
        const target = series.find(s => s.id === i);

        if (target) {
          setSeries.push(target);
        }
      });

      // 表示するシリーズの数が余っている場合、上（額が大きい順？）からセットしていく。
      // If there are extra series to display, set them from the top (in descending order of amount?).
      const seriesCount = this.chartConditions.defaultSeriesCount || this.defaultSeriesCount;
      for (const seriesItem of series) {
        if (setSeries.length > seriesCount) break;

        if (!defaultSeries.includes(seriesItem.id)) {
          setSeries.push(seriesItem);
        }
      }

      // Sort series by value.
      this.currentSeries[this.currentCategory] = sortObject(setSeries, 'value', 'desc');

      this.currentTableSeries = this.convertedCurrentSeries;
    },

    generateChart() {
      this.$refs['chart'].generateChart();
      return Promise.resolve('hero');
    },

    onChangeChartType(val) {
      this.chartType = val;
      this.generateChart();
    },

    onChangeDateRange(range) {
      this.$emit('update-date-range', range);
    },

    onChangeGranularity(value) {
      analyzer.track(`Action: UsageReport analyze change-granularity ${value} `);
      this.$emit('change-granularity', value);
    },

    // Gab: changes here for tags/non-tags category
    async onChangeCategory(value) {
      try {
        this.loading = true;

        if (value !== 'tags') {
          await this.$store.dispatch('reports_v2/resetTagState');
        }

        // If switching to tags, initialize tag data before category change
        if (value === 'tags') {
          await this.fetchTagKeys();
          await this.setInitialTagKey();
          // Only emit change after tag initialization
          this.$emit('change-category', value);
        } else {
          this.$emit('change-category', value);
        }
      } catch (error) {
        console.error('Error in onChangeCategory:', error);
      } finally {
        this.loading = false;
      }
    },

    // Gab: fetching tag keys using reportFilters
    fetchTagKeys() {
      return this.$store.dispatch('reportFilters/fetchFilterAttributes');
    },

    // Gab: setting initial value for current tag key
    async setInitialTagKey() {
      if (!this.tagKeyOptions || this.tagKeyOptions.length === 0) return;

      try {
        // Get sorted tag keys array
        const sortedKeys = [...this.tagKeyOptions].sort((a, b) =>
          a.toLowerCase().localeCompare(b.toLowerCase())
        );

        // Get the first sorted key
        const firstTagKey = sortedKeys[0];
        this.selectedTagKey = firstTagKey;

        // Wait for the tag key to be set in store
        await this.$store.dispatch('reports_v2/setSelectedTagKey', firstTagKey);

        // Trigger a data refresh with the selected tag
        await this.$emit('change-category', 'tags');
      } catch (error) {
        console.error('Failed to set initial tag key:', error);
      }
    },

    // Gab: actions every changes in current tag key
    async onChangeTagKey(selectedOption) {
      try {
        this.loading = true;
        this.selectedTagKey = selectedOption;

        // Wait for tag key to be set
        await this.$store.dispatch('reports_v2/setSelectedTagKey', selectedOption);

        // Trigger data refresh
        await this.$emit('change-category', 'tags');
      } catch (error) {
        console.error('Failed to change tag key:', error);
      } finally {
        this.loading = false;
      }
    },

    // Gab: Validation for tag keys
    hasValidTagData() {
      return this.tagKeyOptions && this.tagKeyOptions.length > 0;
    },

    // Gab: simple tag value naming
    simplifyTagKeyDisplay(tagKey) {
      if (tagKey.startsWith('aws:')) {
        return tagKey.substring(4);
      }
      if (tagKey.startsWith('user:')) {
        return tagKey.substring(5);
      }
      return tagKey;
    },

    onDataValidated(chart) {
      this.chartData = chart.data;
    },

    onOverItem(val) {
      this.over = val;
    },

    onClickItem(val) {
      let granularity;

      switch (this.currentGranularity) {
        case 'daily':
          granularity = 'day';
          break;
        case 'monthly':
          granularity = 'month';
          break;
      }
      analyzer.track(`Action: UsageReport analyze chartitems-${granularity} `);

      const target = this.clickedItem.find(i => moment(i).isSame(val.date, granularity));

      if (target) {
        this.clickedItem = this.clickedItem.filter(i => !moment(i).isSame(val.date, granularity));
      } else {
        this.clickedItem.push(val.date);
      }
    },

    onDeselectClickedItem() {
      this.clickedItem = [];
    },

    async onSubmit() {
      // this.loading = true;

      await this.generateChart();
      this.currentTableSeries = this.convertedCurrentSeries;
    },

    onAppeared() {
      // this.loading = false;
    },

    // Export
    onExportData() {
      const type = this.chartConditions.exportDataType;
      analyzer.track(`Action: UsageReport analyze export ${type} `);
      this.$refs['chart'].exportChart(type);
    },

    onExportImage() {
      const type = this.chartConditions.exportImageType;
      this.$refs['chart'].exportChart(type);
    },

    // Preferences
    onClickChartPreferences() {
      analyzer.track(`Action: UsageReport analyze preferences `);
      this.openPreferencesDialog = true;
    },

    onChangeSettingSuccess() {
      // Reset clicked item for data table
      this.clickedItem = [];

      this.$emit('change-setting-success');
    },

    onSelectAll(value) {
      const op = value.ids === undefined ? this.series : value.ids;
      //this.currentSeries[this.currentCategory] = [];
      op.forEach(i => {
        this.currentSeries[this.currentCategory].push(i.id);
      });
      const distinct = Array.from(new Set(this.currentSeries[this.currentCategory]));
      this.currentSeries[this.currentCategory] = distinct;
    },

    updatePreferences(params) {
      // Reset clicked item for data table
      this.clickedItem = [];

      this.$emit('update-preferences', params);
    },

    updateCurrentCategory(value) {
      this.currentCategory = value;
    },

    onChangeCheckedRegions(value) {
      console.log('onChangeCheckedRegions');
      console.log(value);
    },

    // Gab: for monitoring updates in Usage Report ID (change in ID = sets back category to productCode as default)
    watchId(id) {
      // console.log("Current ID in Usage Report: ", id)
    },

    resetCategoryToProductCode() {
      // console.log('Resetting category to productCode due to ID change');
      this.currentCategory = 'productCode';
    },
  },

  mounted() {
    // Update category to 'tags' when 'selectedTagKey' changes.
    this.$store.watch(
      state => state.reports_v2.selectedTagKey,
      newTagKey => {
        if (newTagKey) {
          this.onUpdateCategory('tags');
        }
      }
    );

    // Listen for the emitted id from index.vue
    this.$root.$on('click-id', (id) => {
      this.watchId(id);
    });

    // Watch the 'id' in Vuex and reset category to 'productCode' when it changes
    this.$store.watch(
      state => state.usageReports.id,
      newId => {
        if (newId) {
          this.resetCategoryToProductCode();
        }
      }
    );
  },
});
</script>

<style lang="scss" scoped>
.__HeroChart {
  &-Content {
    overflow-x: auto;
    display: flex;
    transition: $default-transition;
  }

  &-Content-LeftSide {
    width: 100%;
  }

  // Control
  &-Control {
    display: flex;
    justify-content: space-between;
  }

  &-Control-LeftSide {
    display: flex;
    align-items: center;
  }

  &-Control-RightSide {
    display: flex;
  }

  &-ControlSeparator {
    width: 1px;
    background-color: $neutral-secondary-color;
    margin: $space-xxs $space-sm;
  }

  &-Main {
    position: relative;
  }

  &-Content-RightSide {
  }

  &-Filters {
    width: 280px;
    flex-shrink: 0;
    padding-left: $space-md;
    margin-left: $space-md;
    border-left: 1px solid $border-color;
  }

  &-FiltersFooter {
  }

  &-DataTable {
    margin-top: $space-md;
  }

  .tag-key-container {
    padding-left: 20px;
  }
}
</style>
